<script setup lang="ts">
    import { ref, computed } from 'vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { AlertTypes } from '@shared/components/BsAlerts';
    import FormRadioGroup from '@shared/components/Form/FormRadioGroup.vue';
    import List from '@shared/components/List/List.vue';
    import type { GeoIpTotalStats } from '../../interfaces';
    import { formatNumericText } from '@shared/utils/formatNumericText';

    const props = defineProps<{
        geoIp: GeoIpTotalStats | null;
    }>();

    const showGeoIpBy = ref('country');
    const showByCountry = computed(() => showGeoIpBy.value === 'country');

    const geoIpByOptions = computed(() => [
        { label: $Resources.GeoIpOrderByCountry, value: 'country' },
        { label: $Resources.GeoIpOrderByCity, value: 'city' },
    ]);

    const hasMountainView = () => props.geoIp?.GeoIpCityStats.some((c) => c.CityName === 'Mountain View') ?? false;

    const tableHeaders = computed(() => {
        return [
            showGeoIpBy.value === 'country' ? $Resources.GeoIpOrderByCountry : $Resources.GeoIpOrderByCity,
            $Resources.IssuesOpenedPercentageLabel,
            `${$Resources.IssuesOpenedPercentageLabel} %`,
            $Resources.IssuesClickedPercentageLabel,
            `${$Resources.IssuesClickedPercentageLabel} %`,
        ];
    });

    const orderOptions = [
        { display: $Resources.IssuesOpenedPercentageLabel, key: 'OpenCount' },
        { display: $Resources.IssuesClickedPercentageLabel, key: 'ClickCount' },
    ];
</script>

<template>
    <div class="print-block" v-if="geoIp">
        <h2 class="h3">{{ $Resources.GeoIpTitle }}</h2>

        <BsAlert :show="geoIp.GeoIpCityStats.length === 0" :type="AlertTypes.Info" :dismissible="false" :content="$Resources.IssueStatisticsNoGeoIp" />

        <template v-if="geoIp.GeoIpCityStats.length > 0">
            <FormRadioGroup v-if="geoIp.GeoIpCountryStats.length !== 1" :legend="null" :options="geoIpByOptions" v-model="showGeoIpBy" />

            <BsAlert
                class="m-sm-t"
                :show="hasMountainView()"
                :type="AlertTypes.Info"
                :dismissible="false"
                :title="$Resources.IssueStatisticsGeoIpMountainViewAlertHeading"
                :content="$Resources.IssueStatisticsGeoIpMountainViewAlertBody" />

            <List
                v-if="showByCountry && geoIp.GeoIpCountryStats.length !== 1"
                class="th2-text-right th3-text-right th4-text-right"
                itemId="CountryCode"
                display-as="table"
                :items="geoIp.GeoIpCountryStats"
                :table-headers="tableHeaders"
                :filter-keys="['CountryName']"
                :orderOptions="orderOptions">
                <template #item="{ item }">
                    <td>
                        <span class="flag-icon" :class="`flag-icon-${item.CountryCode.toLowerCase()}`"></span>
                        {{ item.CountryName }}
                    </td>
                    <td class="text-right strong">{{ item.OpenCount }}</td>
                    <td class="text-right strong">{{ formatNumericText(item.OpenPercentage, { suffix: '%' }) }}</td>
                    <td class="text-right strong">{{ item.ClickCount }}</td>
                    <td class="text-right strong">{{ formatNumericText(item.ClickPercentage, { suffix: '%' }) }}</td>
                </template>
            </List>

            <List
                v-if="!showByCountry || geoIp.GeoIpCountryStats.length === 1"
                class="th2-text-right th3-text-right th4-text-right"
                itemId="CityName"
                display-as="table"
                :items="geoIp.GeoIpCityStats"
                :table-headers="tableHeaders"
                :filter-keys="['CityName']"
                :orderOptions="orderOptions">
                <template #item="{ item }">
                    <td>
                        <span class="flag-icon" :class="`flag-icon-${item.CountryCode.toLowerCase()}`"></span>
                        {{ item.CityName }}
                    </td>
                    <td class="text-right strong">{{ item.OpenCount }}</td>
                    <td class="text-right strong">{{ formatNumericText(item.OpenPercentage, { suffix: '%' }) }}</td>
                    <td class="text-right strong">{{ item.ClickCount }}</td>
                    <td class="text-right strong">{{ formatNumericText(item.ClickPercentage, { suffix: '%' }) }}</td>
                </template>
            </List>
        </template>
    </div>
</template>
